import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { getInstituteForUser } from './instituteService';
import { getApiBaseUrl } from './config/apiConfig';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaUserPlus, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaUpload, FaImage, FaTimes, FaSchool } from 'react-icons/fa';
import logo from './logo.png';

export default function InstituteLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState('');
  const emailInputRef = useRef(null);
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    instituteName: '',
    instituteEmail: '',
    phone: '',
    location: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const navigate = useNavigate();

  // Auto-focus email field on mount
  useEffect(() => {
    if (emailInputRef.current && !showRegister) {
      emailInputRef.current.focus();
    }
  }, [showRegister]);

  // Load remembered email from localStorage
  useEffect(() => {
    const shouldRemember = localStorage.getItem('instituteRememberedEmail') === 'true';
    const rememberedEmail = localStorage.getItem('instituteEmail');
    if (shouldRemember && rememberedEmail) {
      setForm(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
    if (error) {
      const timer = setTimeout(() => setError(''), 7000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleRegisterChange = (e) => {
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });
    setRegisterError('');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setRegisterError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setRegisterError('Image size too large (max 5MB)');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setLogoFile(file);
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
    // Reset file input
    const fileInput = document.getElementById('institute-logo-upload');
    if (fileInput) fileInput.value = '';
  };

  const uploadLogoToStorage = async () => {
    if (!logoFile) return null;

    setUploadingLogo(true);
    try {
      // Get current user (will be created during registration)
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || 'temp-' + Date.now();

      const fileExt = logoFile.name.split('.').pop();
      const fileName = `institute-logo-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `institute-logos/${userId}/${fileName}`;

      // Upload to Supabase Storage (using profile-pictures bucket)
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, logoFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Logo upload error:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!form.email || !form.password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    try {
      // Sign in with Supabase Auth
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password,
      });

      if (authError) {
        throw new Error(authError.message || 'Invalid email or password.');
      }

      if (!data.user) {
        throw new Error('Invalid email or password.');
      }

      // Check if user has institute access
      const { data: instituteData, error: instituteError } = await getInstituteForUser(data.user.email);
      
      if (instituteError || !instituteData || !instituteData.institutes) {
        throw new Error('You do not have access to institute dashboard. Please contact your administrator.');
      }

      // Store user info
      localStorage.setItem('userEmail', data.user.email);
      localStorage.setItem('userRole', 'institute');
      
      // Remember email if checkbox is checked
      if (rememberMe) {
        localStorage.setItem('instituteRememberedEmail', 'true');
        localStorage.setItem('instituteEmail', data.user.email);
      } else {
        localStorage.setItem('instituteRememberedEmail', 'false');
        localStorage.removeItem('instituteEmail');
      }
      
      // Always store email for current session
      localStorage.setItem('instituteEmail', data.user.email);
      
      setSuccess('Sign in successful! Redirecting...');
      
      // Redirect to dashboard immediately (no delay needed)
      navigate('/institute', { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetStatus('');
    
    if (!resetEmail) {
      setResetStatus('Please enter your email address.');
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        setResetStatus(`Error: ${error.message}`);
      } else {
        setResetStatus('Password reset email sent! Check your inbox.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
        }, 3000);
      }
    } catch (err) {
      setResetStatus('Failed to send reset email. Please try again.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');

    if (!registerForm.email || !registerForm.password || !registerForm.confirmPassword) {
      setRegisterError('Please fill all required fields.');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterError('Passwords do not match.');
      return;
    }

    if (registerForm.password.length < 6) {
      setRegisterError('Password must be at least 6 characters.');
      return;
    }

    if (!registerForm.instituteName || !registerForm.instituteEmail) {
      setRegisterError('Please enter institute name and email.');
      return;
    }

    setRegisterLoading(true);
    try {
      // Upload logo first if provided
      let logoUrl = null;
      if (logoFile) {
        try {
          logoUrl = await uploadLogoToStorage();
        } catch (logoError) {
          console.error('Logo upload failed:', logoError);
          // Continue with registration even if logo upload fails
          setRegisterError('Logo upload failed, but continuing with registration...');
        }
      }

      // Create auth user first
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: registerForm.email,
        password: registerForm.password,
      });

      if (authError) {
        throw new Error(authError.message || 'Registration failed.');
      }

      // Create institute using backend API (bypasses RLS using service role)
      const registerResponse = await fetch(`${getApiBaseUrl()}/api/register-institute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instituteName: registerForm.instituteName,
          instituteEmail: registerForm.instituteEmail,
          contactEmail: registerForm.email,
          phone: registerForm.phone || null,
          location: registerForm.location || null,
          logoUrl: logoUrl
        })
      });

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json();
        throw new Error(errorData.error || 'Failed to create institute.');
      }

      const { institute: instituteData } = await registerResponse.json();

      // Show success message - don't sign in automatically since status is pending
      setSuccess('Registration successful! Your submission is under review by the Taealam team. You will receive an email once approved.');
      
      // Clear form and switch to login after 7 seconds
      setTimeout(() => {
        setShowRegister(false);
        setRegisterForm({
          email: '',
          password: '',
          confirmPassword: '',
          instituteName: '',
          instituteEmail: '',
          phone: '',
          location: ''
        });
        setLogoPreview(null);
        setLogoFile(null);
        setSuccess('');
      }, 7000);
    } catch (err) {
      setRegisterError(err.message || 'Registration failed.');
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 24,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        width: '100%',
        maxWidth: 480,
        padding: '40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(30, 64, 175, 0.1) 100%)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(30, 64, 175, 0.1) 100%)',
          borderRadius: '50%',
          zIndex: 0
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Logo and Header */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              width: 80,
              height: 80,
              margin: '0 auto 20px',
              background: '#fff',
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(30, 58, 138, 0.3)',
              padding: 12
            }}>
              <img 
                src={logo} 
                alt="Taealam Logo" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain' 
                }} 
              />
            </div>
            <h1 style={{
              fontSize: 28,
              fontWeight: 800,
              color: '#111827',
              margin: '0 0 8px 0'
            }}>
              Institute Portal
            </h1>
            <p style={{
              fontSize: 15,
              color: '#6b7280',
              margin: 0
            }}>
              {showRegister ? 'Create your institute account' : 'Sign in to manage your institute'}
            </p>
          </div>

          {/* Sign In Form */}
          {!showRegister ? (
            <form onSubmit={handleSignIn}>
              {/* Email Field */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 8
                }}>
                  Email Address
                </label>
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FaEnvelope size={18} color="#9ca3af" style={{
                    position: 'absolute',
                    left: 16,
                    zIndex: 1
                  }} />
                  <input
                    ref={emailInputRef}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    autoComplete="email"
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      border: `2px solid ${error && form.email ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: 12,
                      fontSize: 15,
                      transition: 'all 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = '#1e40af'}
                    onBlur={e => e.currentTarget.style.borderColor = error && form.email ? '#ef4444' : '#e5e7eb'}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && form.email && form.password) {
                        handleSignIn(e);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8
                }}>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    margin: 0
                  }}>
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#1e40af',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                  >
                    Forgot Password?
                  </button>
                </div>
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FaLock size={18} color="#9ca3af" style={{
                    position: 'absolute',
                    left: 16,
                    zIndex: 1
                  }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 48px',
                      border: `2px solid ${error && form.password ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: 12,
                      fontSize: 15,
                      transition: 'all 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = '#1e40af'}
                    onBlur={e => e.currentTarget.style.borderColor = error && form.password ? '#ef4444' : '#e5e7eb'}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && form.email && form.password) {
                        handleSignIn(e);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: 16,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                      display: 'flex',
                      alignItems: 'center',
                      color: '#9ca3af'
                    }}
                  >
                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  padding: '12px 16px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: 10,
                  marginBottom: 20,
                  color: '#991b1b',
                  fontSize: 14
                }}>
                  {error}
                </div>
              )}

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: loading 
                    ? '#9ca3af' 
                    : 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: loading 
                    ? 'none' 
                    : '0 4px 12px rgba(30, 58, 138, 0.4)',
                  marginBottom: 24
                }}
                onMouseEnter={e => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(30, 58, 138, 0.5)';
                  }
                }}
                onMouseLeave={e => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.4)';
                  }
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              {/* Register Link */}
              <div style={{
                textAlign: 'center',
                paddingTop: 20,
                borderTop: '1px solid #e5e7eb'
              }}>
                <p style={{
                  fontSize: 14,
                  color: '#6b7280',
                  margin: '0 0 12px 0'
                }}>
                  Don't have an institute account?
                </p>
                <button
                  type="button"
                  onClick={() => setShowRegister(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 24px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  <FaUserPlus size={16} />
                  Register Your Institute
                </button>
              </div>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={handleRegister}>
              {/* Institute Logo Upload */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 8
                }}>
                  Institute Logo
                </label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  alignItems: 'flex-start'
                }}>
                  {logoPreview ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={logoPreview}
                        alt="Institute logo preview"
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: 12,
                          objectFit: 'cover',
                          border: '2px solid #e5e7eb',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }}
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      width: 120,
                      height: 120,
                      borderRadius: 12,
                      border: '2px dashed #d1d5db',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f9fafb',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#1e40af';
                      e.currentTarget.style.background = '#f0f9ff';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.background = '#f9fafb';
                    }}
                    onClick={() => document.getElementById('institute-logo-upload').click()}
                  >
                    <FaImage size={32} color="#9ca3af" />
                  </div>
                  )}
                  <input
                    id="institute-logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingLogo || registerLoading}
                  />
                  <label
                    htmlFor="institute-logo-upload"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      background: uploadingLogo || registerLoading ? '#e5e7eb' : '#f3f4f6',
                      color: uploadingLogo || registerLoading ? '#9ca3af' : '#374151',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: uploadingLogo || registerLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => {
                      if (!uploadingLogo && !registerLoading) {
                        e.currentTarget.style.background = '#e5e7eb';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!uploadingLogo && !registerLoading) {
                        e.currentTarget.style.background = '#f3f4f6';
                      }
                    }}
                  >
                    {uploadingLogo ? (
                      <>
                        <div style={{
                          width: 14,
                          height: 14,
                          border: '2px solid #9ca3af',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite'
                        }} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaUpload size={14} />
                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 8
                }}>
                  Institute Name *
                </label>
                <input
                  type="text"
                  name="instituteName"
                  value={registerForm.instituteName}
                  onChange={handleRegisterChange}
                  placeholder="Your Institute Name"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 12,
                    fontSize: 15,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 8
                }}>
                  Institute Email *
                </label>
                <input
                  type="email"
                  name="instituteEmail"
                  value={registerForm.instituteEmail}
                  onChange={handleRegisterChange}
                  placeholder="institute@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 12,
                    fontSize: 15,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 8
                }}>
                  Your Email (Admin) *
                </label>
                <input
                  type="email"
                  name="email"
                  value={registerForm.email}
                  onChange={handleRegisterChange}
                  placeholder="your@email.com"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 12,
                    fontSize: 15,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={registerForm.password}
                    onChange={handleRegisterChange}
                    placeholder="Min 6 characters"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 12,
                      fontSize: 15,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={registerForm.confirmPassword}
                    onChange={handleRegisterChange}
                    placeholder="Confirm password"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 12,
                      fontSize: 15,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={registerForm.phone}
                    onChange={handleRegisterChange}
                    placeholder="+973 3903 0980"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 12,
                      fontSize: 15,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={registerForm.location}
                    onChange={handleRegisterChange}
                    placeholder="City, Country"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 12,
                      fontSize: 15,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {registerError && (
                <div style={{
                  padding: '12px 16px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: 10,
                  marginBottom: 20,
                  color: '#991b1b',
                  fontSize: 14
                }}>
                  {registerError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="submit"
                  disabled={registerLoading}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: registerLoading 
                      ? '#9ca3af' 
                      : 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: registerLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {registerLoading ? 'Creating...' : 'Register'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRegister(false)}
                  disabled={registerLoading}
                  style={{
                    padding: '16px 24px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: registerLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: 20
            }}
            onClick={() => setShowForgotPassword(false)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 24,
                  maxWidth: 400,
                  width: '100%',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }}
                onClick={e => e.stopPropagation()}
              >
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: '#111827',
                  marginBottom: 12,
                  marginTop: 0
                }}>
                  Reset Password
                </h3>
                <p style={{
                  fontSize: 14,
                  color: '#6b7280',
                  marginBottom: 20
                }}>
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <form onSubmit={handleForgotPassword}>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: 8
                    }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 10,
                        fontSize: 14,
                        boxSizing: 'border-box'
                      }}
                      autoFocus
                    />
                  </div>
                  {resetStatus && (
                    <div style={{
                      padding: '12px 16px',
                      background: resetStatus.includes('sent') ? '#d1fae5' : '#fee2e2',
                      border: `2px solid ${resetStatus.includes('sent') ? '#10b981' : '#ef4444'}`,
                      borderRadius: 10,
                      marginBottom: 20,
                      color: resetStatus.includes('sent') ? '#065f46' : '#991b1b',
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      {resetStatus.includes('sent') ? <FaCheckCircle size={16} /> : <FaInfoCircle size={16} />}
                      <span>{resetStatus}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      type="submit"
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: 'pointer'
                      }}
                    >
                      Send Reset Link
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmail('');
                        setResetStatus('');
                      }}
                      style={{
                        padding: '12px 24px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: 10,
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

